import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  useBreakpoints,
  useIndexResourceState,
  Text,
  Button,
  IndexFilters,
  useSetIndexFiltersMode,
  Avatar,
  Modal,
  Tooltip,
  Box,
  InlineStack,
  Banner,
  Grid,
  ChoiceList,
  BlockStack,
  Icon,
} from '@shopify/polaris';
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';
import {
  InfoIcon, PersonIcon, WorkIcon, LocationIcon, StatusActiveIcon,
  CreditCardIcon, CashDollarIcon, SettingsIcon, PersonAddIcon, ImportIcon,
  CheckIcon, StarFilledIcon, LockIcon, TeamIcon,
} from '@shopify/polaris-icons';
// The same catalogue the pricing cards render, so a price quoted at the wall
// cannot drift from the price on the card it sends the merchant to.
import { BASE_PLANS, formatUsd } from '../Pricing/planCatalog';
import { ColumnHeading } from '../ui/CardSurface';
import { isWix } from '../../utils/platform';
import AllotStaff from '../allotStaff';
import useTrialOffer, { seedTrialOffer } from '../../utils/trialOffer';

const resolveCurrentPlanName = (planId, isFreemium = false) => {
  const id = planId === null || planId === undefined ? null : Number(planId);

  if (id === 1 || id === 5) return 'Standard';
  if (id === 2 || id === 6) return 'Premium';
  if (id === 3) return 'Limited Time Offer';
  if (id === 4 || id === 7) return 'Enterprise';
  if (id === 8) return 'Custom';
  if (!id && (isFreemium === 1 || isFreemium === true)) return 'Free';
  if (!id) return 'Free';

  return 'Standard';
};

const ReusablePricing = ({
  title,
  description,
  PiceingDscrptn,
  CreditsBtn,
  buttonText,
  Checked,
  HeadBtn,
  value,
  Month,
  additionalText,
  icon,
  backgroundColor,
  showIconBox = true,
  isDisable,
  divider,
  features,
  FreeBtn,
  Review,
  InnerCardHeader,
  billingType,
  currentPlan,
  availableStaffs,
  totalStaff,
  shop,
  isLifetimeOffer = false,
  originalPrice,
  discount
}) => {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { t } = useTranslation();

  const handleUpgradeClick = () => {
    navigate('/Pricing');
  };

  // Get plan color based on current plan
  const getPlanColor = () => {
    switch (currentPlan) {
      case 'Free':
        return '#6B7280';
      case 'Standard':
        return '#3B82F6';
      case 'Premium':
        return '#10B981';
      case 'Enterprise':
        return '#F59E0B';
      case 'Custom':
        return '#8B5CF6';
      case 'Limited Time Offer':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  // Get plan badge text
  const getPlanBadgeText = () => {
    switch (currentPlan) {
      case 'Free':
        return t('free_plan');
      case 'Standard':
        return t('standard_plan');
      case 'Premium':
        return t('premium_plan');
      case 'Enterprise':
        return t('enterprise_plan');
      case 'Custom':
        return 'Custom Plan';
      case 'Limited Time Offer':
        return t('unlimited_plan');
      default:
        return t('current_plan');
    }
  };

  const planColor = getPlanColor();
  const planBadgeText = getPlanBadgeText();

  const innerContent = (
    <>
      <Box className='p-6'>
        <InlineStack blockAlign="center" gap="600" align="space-between">
          {/* Left side - Plan Info */}
          <Box className="flex-1">
            <InlineStack gap="300" align="start" blockAlign="center">
              {/* Plan Icon */}
              <Box
                style={{
                  backgroundColor: `${planColor}15`,
                  borderRadius: '12px',
                  padding: '12px',
                  border: `2px solid ${planColor}30`
                }}
              >
                <Box style={{ width: '24px', height: '24px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={planColor} strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </Box>
              </Box>

              {/* Plan Details */}
              <Box className="flex-1">
                <InlineStack gap="200" align="start" blockAlign="center">
                  <Text variant="headingMd" as="h6" fontWeight="bold" style={{ color: planColor }}>
                    {title}
                  </Text>
                  <Badge
                    tone={currentPlan === 'Premium' ? 'success' : currentPlan === 'Limited Time Offer' ? 'warning' : 'info'}
                    size="small"
                  >
                    {planBadgeText}
                  </Badge>
                </InlineStack>

                {description && (
                  <Box paddingBlockStart="200">
                    <InlineStack gap="400" align="start" blockAlign="center">
                      {/* Staff Info */}
                      <InlineStack gap="100" align="center">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        <Text variant="bodyMd" as="p" color="subdued" fontWeight="medium">
                          {description}
                        </Text>
                      </InlineStack>

                      {/* Usage Indicator - Only show if not unlimited */}
                      {availableStaffs !== "Unlimited" && availableStaffs !== "unlimited" && typeof availableStaffs === 'number' && availableStaffs > 0 && (
                        <InlineStack gap="100" align="center">
                          <Box style={{
                            width: '60px',
                            height: '6px',
                            backgroundColor: '#E5E7EB',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <Box
                              className="usage-bar"
                              style={{
                                width: `${Math.min((totalStaff || 0) / availableStaffs * 100, 100)}%`,
                                height: '100%',
                                backgroundColor: planColor,
                                borderRadius: '3px',
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </Box>
                          <Text variant="bodySm" color="subdued">
                            {Math.round((totalStaff || 0) / availableStaffs * 100)}% {t('used')}
                          </Text>
                          {totalStaff >= availableStaffs && (
                            <Text variant="bodySm" tone="critical" fontWeight="bold">
                              {t('staff_limit_reached')}
                            </Text>
                          )}
                        </InlineStack>
                      )}

                      {/* Unlimited indicator */}
                      {(availableStaffs === "Unlimited" || availableStaffs === "unlimited") && (
                        <InlineStack gap="100" align="center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
                            <path d="M9 12l2 2 4-4" />
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                          <Text variant="bodySm" color="success" fontWeight="medium">
                            {t('unlimited_staff_available')}
                          </Text>
                        </InlineStack>
                      )}
                    </InlineStack>
                  </Box>
                )}
              </Box>
            </InlineStack>
          </Box>

          {/* Right side - Action Button */}
          <Box className="flex-shrink-0">
            <InlineStack gap="200" align="center" blockAlign="center">
              {/* Upgrade Benefits */}
              {(currentPlan === 'Free' || currentPlan === 'Standard') && (
                <Box style={{ textAlign: 'right' }}>
                  <Text variant="bodySm" color="subdued" fontWeight="medium">
                    {t('upgrade_to_add_more_staff')}
                  </Text>
                </Box>
              )}

              <Button
                variant="primary"
                onClick={handleUpgradeClick}
                style={{
                  background: currentPlan === 'Free' || currentPlan === 'Standard'
                    ? `linear-gradient(135deg, ${planColor}, ${planColor}dd)`
                    : planColor,
                  border: 'none',
                  boxShadow: `0px 4px 12px ${planColor}30`,
                  fontWeight: '600',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease'
                }}
                className="hover:scale-105 transition-transform"
              >
                <InlineStack gap="100" align="center">
                  {currentPlan === 'Free' || currentPlan === 'Standard' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  )}
                  {buttonText}
                </InlineStack>
              </Button>
            </InlineStack>
          </Box>
        </InlineStack>
      </Box>
    </>
  );

  return (
    <Box
      className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-lg hover:shadow-xl transition-all duration-300"
      style={{
        background: `linear-gradient(135deg, #ffffff 0%, ${planColor}05 100%)`,
        borderColor: `${planColor}20`
      }}
    >
      <Card padding={0} borderRadius="0">
        {innerContent}
      </Card>

      {/* Add a subtle glow effect on hover */}
      <style jsx>{`
        .hover\\:shadow-xl:hover {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px ${planColor}20;
        }
        
        .hover\\:scale-105:hover {
          transform: scale(1.02);
        }
        
        /* Add a subtle pulse animation for the usage bar */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        .usage-bar {
          animation: pulse 2s infinite;
        }
      `}</style>
    </Box>
  );
};

const StaffManagement = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { mode, setMode } = useSetIndexFiltersMode();
  const breakpoints = useBreakpoints();
  const isMobile = breakpoints.smDown;     // <-- current viewport test
  const { t } = useTranslation();
  const [deleteAction, setDeleteAction] = useState('archive'); // 'archive' or 'delete'
  /* ------------------------------------------------------------------
     STATE
  ------------------------------------------------------------------ */
  const [searchValue, setSearchValue] = useState('');
  const [selectedStatusTab, setSelectedStatusTab] = useState(0);
  const [staffs, setStaffs] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [singleStaffDelete, setSingleStaffDelete] = useState(null);
  const { trialDays: planTrialDays } = useTrialOffer(axiosClient);

  // The seat wall: what a merchant meets when they ask for a seat they do not
  // have. Peak purchase intent, so it shows the cost and the way forward.
  const [seatWallOpen, setSeatWallOpen] = useState(false);
  const [seatWallBusy, setSeatWallBusy] = useState(false);
  const [seatWallError, setSeatWallError] = useState('');

  const [shopInfo, setShopInfo] = useState({
    paid_available_staffs: 0,
    total_staff: 0,
    plan_id: null,
    has_capped_amount: false,
  });
  const [showLimitBanner, setShowLimitBanner] = useState(true);
  const [showPremiumBanner, setShowPremiumBanner] = useState(true);
  const [currentPlan, setCurrentPlan] = useState('');
  const [shop, setShop] = useState('');
  const [syncingSeats, setSyncingSeats] = useState(false);

  /* ------------------------------------------------------------------
     HELPERS
  ------------------------------------------------------------------ */
  const getInitials = (firstName, lastName) =>
    `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

  const planSupportsOverage = () => {
    const isPlan1WithCap = shopInfo.plan_id === 1 && shopInfo.has_capped_amount;
    return isPlan1WithCap
      || shopInfo.plan_id === 2
      || shopInfo.plan_id === 4
      || shopInfo.plan_id === 5
      || shopInfo.plan_id === 6
      || shopInfo.plan_id === 7
      || shopInfo.plan_id === 8;
  };

  // Check if staff limit is reached (archived staff never count toward the limit)
  const isStaffLimitReached = () => {
    if (shopInfo.paid_available_staffs === "Unlimited" || shopInfo.paid_available_staffs === "unlimited") {
      return false;
    }

    const availableStaffs = Number(shopInfo.paid_available_staffs);
    const totalStaff = Number(shopInfo.total_staff);

    if (isNaN(availableStaffs) || isNaN(totalStaff)) {
      return false;
    }

    // Pay-as-you-go plans bill overage on add — never block the button.
    if (planSupportsOverage()) {
      return false;
    }

    return totalStaff >= availableStaffs;
  };

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axiosClient.get('/getStaff');
      if (data.success) {
        const {
          staff,
          paid_available_staffs,
          total_staff,
          name,
          currency,
          plan_id,
          has_capped_amount,
        } = data;

        const availableStaffs = (paid_available_staffs === "Unlimited" || paid_available_staffs === "unlimited")
          ? "Unlimited"
          : Number(paid_available_staffs) || 0;
        const totalStaffCount = Number(total_staff) || 0;

        const updatedStaff = staff.map((s) => ({
          ...s,
          disabled: !!s.is_disabled,
        }));

        setStaffs(updatedStaff);
        setShopInfo({
          paid_available_staffs: availableStaffs,
          total_staff: totalStaffCount,
          name,
          currency,
          plan_id: +plan_id,
          has_capped_amount,
        });
        setCurrentPlan((prev) => prev || resolveCurrentPlanName(plan_id, false));
      }
    } catch (e) {
      // Error handled silently
    }
  }, []);

  const handleSyncStaffSeats = async () => {
    setSyncingSeats(true);
    try {
      const { data } = await axiosClient.post('/stripe/sync-staff-seats');
      if (data.success) {
        await fetchStaff();
        alert(data.message || 'Subscription updated.');
      } else {
        alert(data.message || 'Could not purchase extra seats.');
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Could not purchase extra seats.');
    } finally {
      setSyncingSeats(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  /* ------------------------------------------------------------------
     EFFECT: FETCH PLAN INFORMATION
  ------------------------------------------------------------------ */
  useEffect(() => {
    (async () => {
      try {
        const response = await axiosClient.get('/db_plans');
        const userEventData = response.data.data;
        setShop(userEventData.shop);
        // This screen fetches /db_plans anyway, so hand the answer to the
        // shared cache rather than making it ask again.
        seedTrialOffer(userEventData);
        setCurrentPlan(resolveCurrentPlanName(userEventData.user_plan_id, userEventData.shopify_freemium));
      } catch (error) {
        // Error handled silently
      }
    })();
  }, []);

  /* ------------------------------------------------------------------
     FILTERS, SEARCH, SELECTION
  ------------------------------------------------------------------ */
  const statusTabs = [
    { id: 'all', content: 'All' },
    { id: 'active', content: t('active') },
    { id: 'deactive', content: t('inactive') },
    { id: 'missing_payment', content: t('missing_payment_info') },
    { id: 'archived', content: 'Archived' }, // New tab
  ];

let filteredStaffs = [...staffs];

  if (statusTabs[selectedStatusTab]?.id === 'active')
    filteredStaffs = filteredStaffs.filter(s => s.status === 'Active');
  else if (statusTabs[selectedStatusTab]?.id === 'deactive')
    filteredStaffs = filteredStaffs.filter(s => ['Deactive', 'Inactive'].includes(s.status));
  else if (statusTabs[selectedStatusTab]?.id === 'missing_payment')
    filteredStaffs = filteredStaffs.filter(s => !s.payment_method?.trim());
  else if (statusTabs[selectedStatusTab]?.id === 'archived')
    filteredStaffs = filteredStaffs.filter(s => s.status === 'Archived');
  // 'all' tab shows everyone including archived

  if (searchValue)
    filteredStaffs = filteredStaffs.filter(({ first_name, last_name, position = '' }) => {
      const full = `${first_name} ${last_name}`.toLowerCase();
      return full.includes(searchValue.toLowerCase()) ||
        position.toLowerCase().includes(searchValue.toLowerCase());
    });

  const resourceName = { singular: 'staff member', plural: 'staff members' };

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
    clearSelection,
  } = useIndexResourceState(filteredStaffs, {
    resourceIDResolver: ({ id }) => id?.toString() || '',
    selectionDisabled: s => s.disabled,
  });

  /* ------------------------------------------------------------------
     ACTIONS
  ------------------------------------------------------------------ */
  const handleClearAll = useCallback(() => {
    setSearchValue('');
    setSelectedFilters([]);
  }, []);

  const handleRowClick = (id, disabled) => !disabled && navigate(`/StaffManagement/staffDetails/${id}`);

  const handleEditStaff = (staffId, event) => {
    event.stopPropagation(); // Prevent row click
    const staff = staffs.find(s => s.id === staffId);
    if (staff?.disabled) {
      alert(t('disabled_staff_cannot_be_edited'));
      return;
    }
    navigate(`/StaffManagement/editStaff/${staffId}`);
  };

  const handleUnarchiveStaff = async (staffId, event) => {
    event.stopPropagation();
    if (!window.confirm('Unarchive this staff member?')) return;
    try {
      const response = await axiosClient.post('/unarchive-staff', { id: staffId });
      if (response.data.success) {
        const resolvedStatus = response.data.status;
        setStaffs(prev =>
          prev.map(s => s.id === staffId ? { ...s, status: resolvedStatus } : s)
        );
        setShopInfo(prev => ({
          ...prev,
          total_staff: prev.total_staff + 1,
        }));
      } else {
        alert(response.data.message || 'Failed to unarchive staff.');
      }
    } catch (e) {
      // 422 = limit reached — show the descriptive message from the backend
      const msg = e.response?.data?.message || 'Error unarchiving staff.';
      alert(msg);
    }
  };

  const handleDeleteStaff = (staffId, event) => {
    event.stopPropagation(); // Prevent row click
    const staff = staffs.find(s => s.id === staffId);
    if (staff?.disabled) {
      alert(t('disabled_staff_cannot_be_deleted'));
      return;
    }
    // Store the staff info for single deletion
    setSingleStaffDelete(staff);
    setActiveDeleteModal(true);
  };

  // Check if every selected row is archived (drives which bulk actions show)
  const allSelectedAreArchived = selectedResources.length > 0 &&
    selectedResources.every(id => staffs.find(s => s.id.toString() === id)?.status === 'Archived');

  const handleBulkUnarchive = async () => {
    if (!selectedResources.length) return;
    const ids = selectedResources.map(Number);
    try {
      const results = await Promise.allSettled(
        ids.map(id => axiosClient.post('/unarchive-staff', { id }))
      );

      const statusMap = {};
      const failed = [];

      results.forEach((result, i) => {
        if (result.status === 'fulfilled' && result.value.data.success) {
          statusMap[ids[i]] = result.value.data.status;
        } else {
          // Capture the backend message (limit reached or other error)
          const msg = result.reason?.response?.data?.message
            || result.value?.data?.message
            || 'Failed';
          failed.push(msg);
        }
      });

      const unarchived = Object.keys(statusMap).map(Number);

      if (unarchived.length > 0) {
        setStaffs(prev =>
          prev.map(s => unarchived.includes(s.id) ? { ...s, status: statusMap[s.id] } : s)
        );
        setShopInfo(prev => ({
          ...prev,
          total_staff: prev.total_staff + unarchived.length,
        }));
      }

      clearSelection();

      if (failed.length > 0) {
        // Deduplicate messages and show once
        const unique = [...new Set(failed)];
        alert(`${unarchived.length} unarchived. ${failed.length} failed:\n${unique.join('\n')}`);
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Error unarchiving staff.');
    }
  };

  const promotedBulkActions = [
    {
      content: t('edit_staff'),
      onAction: () => {
        if (selectedResources.length !== 1) return alert(t('select_exactly_one_staff_member_to_edit'));
        const staff = staffs.find(s => s.id.toString() === selectedResources[0]);
        if (staff?.disabled) return alert(t('disabled_staff_cannot_be_edited'));
        navigate(`/StaffManagement/editStaff/${staff.id}`);
      },
    },
    // Archive bulk action — only shown when selected staff are NOT all archived
    ...(!allSelectedAreArchived ? [{
      content: 'Archive Staff',
      onAction: () => {
        if (!selectedResources.length) return;
        const hasArchived = selectedResources.some(
          id => staffs.find(s => s.id.toString() === id)?.status === 'Archived'
        );
        if (hasArchived) return alert('One or more selected staff are already archived.');
        setSingleStaffDelete(null);
        setDeleteAction('archive');
        setActiveDeleteModal(true);
      },
    }] : []),
    // Unarchive bulk action — only shown when ALL selected are archived
    ...(allSelectedAreArchived ? [{
      content: 'Unarchive Staff',
      onAction: handleBulkUnarchive,
    }] : []),
    {
      content: t('delete_staff'),
      onAction: () => {
        if (!selectedResources.length) return alert(t('select_staff_to_delete'));
        if (selectedResources.some(id => staffs.find(s => s.id.toString() === id)?.disabled))
          return alert(t('disabled_staff_cannot_be_deleted'));
        setSingleStaffDelete(null);
        setDeleteAction('delete');
        setActiveDeleteModal(true);
      },
    },
  ];

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const idsToDelete = (singleStaffDelete ? [singleStaffDelete.id] : selectedResources).map(String);

      const response = await axiosClient.post('/delete-staff', {
        data: {
          ids: idsToDelete,
          action: deleteAction,
        },
      });

      if (response.data.success) {
        if (deleteAction === 'archive') {
          // Update status to Archived and decrement total_staff (archived don't count toward limit)
          setStaffs(prev =>
            prev.map(s =>
              idsToDelete.includes(s.id.toString()) ? { ...s, status: 'Archived' } : s
            )
          );
          setShopInfo(prev => ({
            ...prev,
            total_staff: prev.total_staff - idsToDelete.length,
          }));
        } else {
          // Hard delete — remove from state entirely
          setStaffs(prev => prev.filter(s => !idsToDelete.includes(s.id.toString())));
          setShopInfo(prev => ({
            ...prev,
            total_staff: prev.total_staff - idsToDelete.length,
          }));
        }

        clearSelection();
        setSingleStaffDelete(null);
        setDeleteAction('archive');
      } else {
        alert(response.data.message || t('failed_to_delete_staff'));
      }
    } catch (e) {
      alert(e.response?.data?.message || t('error_deleting_staff'));
    } finally {
      setIsDeleting(false);
      setActiveDeleteModal(false);
    }
  };

  /**
   * Cheapest plan whose seat cap covers the team the merchant is trying to
   * build. Reads the same catalogue the pricing cards render, so the number
   * quoted here cannot drift from the number on the card they land on.
   */
  const recommendedPlan = () => {
    const needed = (Number(shopInfo.total_staff) || 0) + 1;

    return [BASE_PLANS.standard, BASE_PLANS.premium, BASE_PLANS.enterprise]
      .find((plan) => plan.staff >= needed) || BASE_PLANS.enterprise;
  };

  /**
   * Straight to Stripe Checkout for the recommended plan.
   *
   * Same endpoint and same argument shape the pricing page posts, so both
   * routes are priced by the one server-side resolver - the client never names
   * a price. Monthly, because the wall recommends a monthly figure.
   */
  const startSeatWallCheckout = async (plan) => {
    const { data } = await axiosClient.post('/stripe/create-checkout-session', {
      plan: plan.key,
      interval: 'monthly',
      staffCount: plan.staff,
    });

    if (!data?.url) {
      throw new Error(data?.message || 'Checkout session had no URL');
    }

    window.location.href = data.url;
  };

  const handleAddStaffClick = () => {
    if (isStaffLimitReached()) {
      // Peak purchase intent: show what it costs and where to go, rather than
      // a dead control. Never an alert() - a browser dialog cannot carry a call
      // to action, and the disabled button this replaces never fired at all.
      setSeatWallOpen(true);
      return;
    }

    navigate('/StaffManagement/AddNonShopifyStaffPage');
  };

  const renderAddButton = () => {
    const limitReached = isStaffLimitReached();
    let buttonText = t('add_staff');

    // Calculate if this is an overage addition
    const availableStaffs = Number(shopInfo.paid_available_staffs);
    const totalStaff = Number(shopInfo.total_staff);
    const isOverage = !isNaN(availableStaffs) && !isNaN(totalStaff) && totalStaff >= availableStaffs;

    // Customize text for Pay-As-You-Go
    if (isOverage) {
      let overagePrice = null;

      if ((shopInfo.plan_id === 1 && shopInfo.has_capped_amount) || shopInfo.plan_id === 5) {
        overagePrice = 5;
      } else if (shopInfo.plan_id === 2 || shopInfo.plan_id === 6) {
        overagePrice = 4;
      } else if (shopInfo.plan_id === 4 || shopInfo.plan_id === 7) {
        overagePrice = 2;
      } else if (shopInfo.plan_id === 8) {
        overagePrice = 6;
      }

      if (overagePrice) {
        buttonText = `Add Staff (+$${overagePrice}/mo)`;
      }
    }

    // If limit is not reached, render button without tooltip
    if (!limitReached) {
      return (
        <InlineStack gap="200">
          <Button
            variant="primary"
            icon={PersonAddIcon}
            onClick={handleAddStaffClick}
          >
            {buttonText}
          </Button>
          <Button
            variant="secondary"
            icon={ImportIcon}
            onClick={() => navigate('/StaffManagement/bulk-import')}
          >
            {t('bulk_import')}
          </Button>
        </InlineStack>
      );
    }

    // At the cap the primary button stays ENABLED: clicking it opens the
    // upgrade dialog. A disabled button swallows its own onClick, and a tooltip
    // does not exist on touch, so the old version simply did nothing on a phone
    // and showed a browser alert everywhere else.
    return (
      <InlineStack gap="200">
        <Button
          variant="primary"
          icon={PersonAddIcon}
          onClick={handleAddStaffClick}
        >
          {buttonText}
        </Button>
        <Button
          variant="secondary"
          icon={ImportIcon}
          onClick={() => navigate('/StaffManagement/bulk-import')}
          disabled={limitReached}
        >
          {t('bulk_import')}
        </Button>
      </InlineStack>
    );
  };

  /* ------------------------------------------------------------------
     RENDER
  ------------------------------------------------------------------ */
  return (
    <Page fullWidth>
      <Layout>
        <Layout.Section>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Text as="h1" variant="headingLg">{t('staff_management')}</Text>
            {/* The page's actions take the slot the wordmark had. They used to
                sit in a band of their own between the plan card and the table:
                36px of buttons with 32px of margin around them, for two
                controls that belong beside the title. */}
            {renderAddButton()}
          </div>
          {/* The seat offer, above the plan card it refers to - the same place
              the Shopify app puts it.

              Hidden on Wix for the same reason the plan card below is: App
              Market apps must bill through Wix Billing, and an offer that ends
              in our own chat granting a seat is a billing change made outside
              it.

              It takes the REAL seat state so its headline can name the wall the
              merchant is actually looking at. It never prints these numbers -
              that is the plan card's job - it only chooses which sentence to
              say, and it returns null until they have loaded, so nobody is told
              their limit is reached while the request is still in flight. */}
          {/* The gap belongs to the page, not to the banner: a component that
              carries its own outer margin cannot be placed anywhere else
              without fighting it. Same wrapper the Shopify app uses. */}
          {!isWix() && (
            <div className="mb-4">
              <AllotStaff
                seatCap={shopInfo.paid_available_staffs}
                seatsUsed={shopInfo.total_staff}
                planName={currentPlan}
                isLimitReached={isStaffLimitReached()}
              />
            </div>
          )}

          {/* Current plan card. Hidden on Wix: App Market apps must bill through
              Wix Billing, so an "Upgrade Plan" CTA pointing at our own checkout
              is a compliance rejection trigger. */}
          {!isWix() && (
          <Box paddingBlockEnd="400">
            <Grid columns={{ xs: 1, sm: 1, md: 1, lg: 1 }} gap="300">
              <ReusablePricing
                title={`${t('current_plan')}: ${currentPlan || 'N/A'}`}
                description={`${t('available_staffs')}: ${shopInfo.paid_available_staffs === 'Unlimited' || shopInfo.paid_available_staffs === 'unlimited'
                  ? t('unlimited')
                  : (shopInfo.paid_available_staffs || 'N/A')} | ${t('total_staff')}: ${shopInfo.total_staff || 0}`}
                buttonText={currentPlan === 'Free' || currentPlan === 'Standard' ? t('upgrade_plan') : t('manage_plan')}
                icon={<svg width="16" height="16" viewBox="0 0 22 22" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 12.5C10.0717 12.5 9.1815 12.8687 8.52513 13.5251C7.86875 14.1815 7.5 15.0717 7.5 16C7.5 16.9283 7.86875 17.8185 8.52513 18.4749C9.1815 19.1313 10.0717 19.5 11 19.5C11.9283 19.5 12.8185 19.1313 13.4749 18.4749C14.1313 17.8185 14.5 16.9283 14.5 16C14.5 15.0717 14.1313 14.1815 13.4749 13.5251C12.8185 12.8687 11.9283 12.5 11 12.5ZM9.5 16C9.5 15.6022 9.65804 15.2206 9.93934 14.9393C10.2206 14.658 10.6022 14.5 11 14.5C11.3978 14.5 11.7794 14.658 12.0607 14.9393C12.342 15.2206 12.5 15.6022 12.5 16C12.5 16.3978 12.342 16.7794 12.0607 17.0607C11.7794 17.342 11.3978 17.5 11 17.5C10.6022 17.5 10.2206 17.342 9.93934 17.0607C9.65804 16.7794 9.5 16.3978 9.5 16Z" />
                  <path d="M16.526 5.116L13.347 0.658997L1.658 9.997L1.01 9.99V10H0.5V22H21.5V10H20.538L18.624 4.401L16.526 5.116ZM18.425 10H8.397L15.866 7.454L17.388 6.967L18.425 10ZM14.55 5.79L6.84 8.418L12.946 3.54L14.55 5.79ZM2.5 18.169V13.829C2.92218 13.68 3.30565 13.4384 3.62231 13.1219C3.93896 12.8054 4.18077 12.4221 4.33 12H17.67C17.8191 12.4223 18.0609 12.8058 18.3775 13.1225C18.6942 13.4391 19.0777 13.6809 19.5 13.83V18.17C19.0777 18.3191 18.6942 18.5609 18.3775 18.8775C18.0609 19.1942 17.8191 19.5777 17.67 20H4.332C4.18218 19.5777 3.93996 19.1941 3.62302 18.8774C3.30607 18.5606 2.9224 18.3188 2.5 18.169Z" />
                </svg>}
                backgroundColor="#f1f8ff"
                currentPlan={currentPlan}
                availableStaffs={shopInfo.paid_available_staffs}
                totalStaff={shopInfo.total_staff}
                shop={shop}
              />
            </Grid>
          </Box>
          )}

          {shopInfo.paid_available_staffs !== 'Unlimited' &&
            shopInfo.paid_available_staffs !== 'unlimited' &&
            shopInfo.paid_available_staffs < shopInfo.total_staff &&
            showLimitBanner && (
              <Box paddingBlockEnd="400">
                <Banner
                  tone="warning"
                  title={`${t('staff_limit_exceeded')}: ${shopInfo.paid_available_staffs} ${t('of_active')} ${shopInfo.total_staff}`}
                  onDismiss={() => setShowLimitBanner(false)}
                  action={
                    isWix()
                      ? undefined
                      : shopInfo.plan_id === 1
                      ? { content: t('upgrade_plan'), onAction: () => navigate('/Pricing') }
                      : planSupportsOverage()
                        ? {
                            content: syncingSeats ? 'Processing…' : 'Purchase seats',
                            onAction: handleSyncStaffSeats,
                            loading: syncingSeats,
                          }
                        : undefined
                  }
                >
                  <p>
                    {(() => {
                      let price = '$5';
                      if (shopInfo.plan_id === 2 || shopInfo.plan_id === 6) price = '$4';
                      else if (shopInfo.plan_id === 4 || shopInfo.plan_id === 7) price = '$2';
                      else if (shopInfo.plan_id === 8) price = '$6';

                      return (shopInfo.plan_id === 1 || shopInfo.plan_id === 5)
                        ? t('additional_staff_members_have_been_disabled')
                        : planSupportsOverage()
                          ? t('extra_staff_will_be_charged', { price })
                          : t('upgrade_your_plan_to_enable_all_staff_members');
                    })()}
                  </p>
                </Banner>
              </Box>
            )}

          {/* Table card ------------------------------------------------------------ */}
          <Card>
            <IndexFilters
              queryValue={searchValue}
              queryPlaceholder={t('search_staff')}
              onQueryChange={setSearchValue}
              onQueryClear={() => setSearchValue('')}
              cancelAction={{ onAction: handleClearAll }}
              tabs={statusTabs}
              selected={selectedStatusTab}
              onSelect={setSelectedStatusTab}
              filters={[]}
              appliedFilters={selectedFilters}
              onClearAll={handleClearAll}
              mode={mode}
              setMode={setMode}
              canCreateNewView={false}
            />

            {/* ----- Horizontal scroll wrapper (only on phones) ----- */}
            <div
              style={
                isMobile
                  ? {
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }
                  : undefined
              }
            >
              <IndexTable
                resourceName={resourceName}
                itemCount={filteredStaffs.length}
                selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                onSelectionChange={(id, checked, selection) => {
                  const staff = staffs.find(s => s.id.toString() === id);
                  if (!staff?.disabled) handleSelectionChange(id, checked, selection);
                }}
                headings={[
                  { id: 'name', title: <ColumnHeading icon={PersonIcon}>{t('name')}</ColumnHeading> },
                  { id: 'position', title: <ColumnHeading icon={WorkIcon}>{t('position')}</ColumnHeading> },
                  { id: 'location', title: <ColumnHeading icon={LocationIcon}>{t('location')}</ColumnHeading> },
                  { id: 'status', title: <ColumnHeading icon={StatusActiveIcon}>{t('status')}</ColumnHeading> },
                  { id: 'payment_method', title: <ColumnHeading icon={CreditCardIcon}>{t('payment_method')}</ColumnHeading> },
                  { id: 'payroll_type', title: <ColumnHeading icon={CashDollarIcon}>{t('payroll_type')}</ColumnHeading> },
                  { id: 'actions', title: <ColumnHeading icon={SettingsIcon}>{t('actions')}</ColumnHeading> },
                ]}
                selectable
                promotedBulkActions={promotedBulkActions}
                style={{ minWidth: 920 }}
              >
                {filteredStaffs.map((staff, index) => {
                  const disabled = staff.disabled;
                  return (
                    <IndexTable.Row
                      id={staff.id?.toString() || index.toString()}
                      key={staff.id?.toString() || index.toString()}
                      selected={selectedResources.includes(staff.id?.toString())}
                      position={index}
                      onClick={() => handleRowClick(staff.id, disabled)}
                      disabled={disabled}
                      style={{
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                        backgroundColor: disabled ? '#f9fafb' : 'transparent',
                      }}
                    >
                      <IndexTable.Cell>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                          <Avatar
                            name={`${staff.first_name} ${staff.last_name}`}
                            initials={getInitials(staff.first_name, staff.last_name)}
                          />
                          <div>
                            <Text variant="bodyMd" fontWeight="bold">
                              {staff.first_name} {staff.last_name}
                              {disabled && (
                                <Tooltip content={t('disabled_staff_limit_reached')}>
                                  <span style={{ marginInlineStart: 6 }}>
                                    <svg width="12" height="12" viewBox="0 0 20 20">
                                      <path
                                        fill="#8C9196"
                                        d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9V9h2v4zm0-5H9V6h2v2z"
                                      />
                                    </svg>
                                  </span>
                                </Tooltip>
                              )}
                            </Text>
                            {staff.email && (
                              <Text variant="bodySm" color="subdued">
                                {staff.email}
                              </Text>
                            )}
                          </div>
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{staff.position || 'N/A'}</IndexTable.Cell>
                      <IndexTable.Cell>{staff.locations || 'N/A'}</IndexTable.Cell>
                      <IndexTable.Cell>
                        {staff.status === 'Archived' ? (
                          <Badge tone="critical">Archived</Badge>
                        ) : disabled ? (
                          <Badge tone="critical">{t('disabled')}</Badge>
                        ) : staff.status === 'Active' ? (
                          <Badge tone="success">{t('active')}</Badge>
                        ) : (
                          <Tooltip content={t('staff_auto_active_msg')}>
                            <span style={{ cursor: 'help' }}>
                              <Badge tone="warning" icon={InfoIcon}>
                                {t('inactive')}
                              </Badge>
                            </span>
                          </Tooltip>
                        )}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {staff.payment_method || <Badge tone="warning">{t('pending')}</Badge>}
                      </IndexTable.Cell>
                      <IndexTable.Cell>{staff.payroll_type || 'N/A'}</IndexTable.Cell>
                      <IndexTable.Cell>
                        <InlineStack gap="200" align="start">
                          {staff.status === 'Archived' ? (
                            /* Archived staff: only show Unarchive button */
                            <Tooltip content="Unarchive Staff">
                              <Button
                                size="micro"
                                variant="tertiary"
                                icon={
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
                                    <polyline points="8 12 12 8 16 12" />
                                  </svg>
                                }
                                onClick={(event) => handleUnarchiveStaff(staff.id, event)}
                                accessibilityLabel="Unarchive staff"
                              />
                            </Tooltip>
                          ) : (
                            <>
                              {/* 1. Edit Button */}
                              <Tooltip content={disabled ? t('disabled_staff_cannot_be_edited') : t('edit_staff')}>
                                <Button
                                  size="micro"
                                  variant="tertiary"
                                  icon={
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M12 20h9" />
                                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                    </svg>
                                  }
                                  onClick={(event) => handleEditStaff(staff.id, event)}
                                  disabled={disabled}
                                  accessibilityLabel={t('edit_staff')}
                                />
                              </Tooltip>

                              {/* 2. Archive Button */}
                              <Tooltip content="Archive Staff">
                                <Button
                                  size="micro"
                                  variant="tertiary"
                                  icon={
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
                                    </svg>
                                  }
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSingleStaffDelete(staff);
                                    setDeleteAction('archive');
                                    setActiveDeleteModal(true);
                                  }}
                                  disabled={disabled}
                                  accessibilityLabel="Archive staff"
                                />
                              </Tooltip>

                              {/* 3. Delete Button */}
                              <Tooltip content={disabled ? t('disabled_staff_cannot_be_deleted') : t('delete_staff')}>
                                <Button
                                  size="micro"
                                  variant="tertiary"
                                  tone="critical"
                                  icon={
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="3,6 5,6 21,6" />
                                      <path d="M19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2" />
                                      <line x1="10" y1="11" x2="10" y2="17" />
                                      <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                  }
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSingleStaffDelete(staff);
                                    setDeleteAction('delete');
                                    setActiveDeleteModal(true);
                                  }}
                                  disabled={disabled}
                                  accessibilityLabel={t('delete_staff')}
                                />
                              </Tooltip>
                            </>
                          )}
                        </InlineStack>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  );
                })}
              </IndexTable>
            </div>
          </Card>

          {/* Delete confirmation modal */}
          <Modal
            open={activeDeleteModal}
            onClose={() => {
              setActiveDeleteModal(false);
              setSingleStaffDelete(null);
              setDeleteAction('archive'); // Reset on close
            }}
            title={t('remove_staff')}
            primaryAction={{
              content: deleteAction === 'archive' ? 'Archive' : 'Permanently Delete',
              destructive: deleteAction === 'delete', // Only red if permanently deleting
              onAction: handleConfirmDelete,
              loading: isDeleting,
            }}
            secondaryActions={[{
              content: t('cancel'),
              onAction: () => {
                setActiveDeleteModal(false);
                setSingleStaffDelete(null);
                setDeleteAction('archive');
              }
            }]}
          >
            <Modal.Section>
              <BlockStack gap="400">
                <Text variant="bodyMd">
                  {singleStaffDelete
                    ? `How would you like to remove "${singleStaffDelete.first_name} ${singleStaffDelete.last_name}"?`
                    : "How would you like to remove the selected staff members?"
                  }
                </Text>
                
                <ChoiceList
                  title="Removal method"
                  titleHidden
                  choices={[
                    {
                      label: 'Archive (Recommended)',
                      helpText: 'Preserves historical payroll and clock-in data, but removes their access and frees up a staff seat.',
                      value: 'archive',
                    },
                    {
                      label: 'Permanently Delete',
                      helpText: 'Permanently erases all data associated with this staff member. This cannot be undone.',
                      value: 'delete',
                    },
                  ]}
                  selected={[deleteAction]}
                  onChange={(value) => setDeleteAction(value[0])}
                />
              </BlockStack>
            </Modal.Section>
          </Modal>

          {seatWallOpen && (() => {
            const plan = recommendedPlan();
            const cap = shopInfo.paid_available_staffs;
            const total = Number(shopInfo.total_staff) || 0;
            // Only offered when the server says this account would actually get
            // it - see utils/trialOffer for the rule the checkout also applies.
            const trialDays = planTrialDays(plan.key);
            // The meter reflects real, server-supplied numbers. An unlimited or
            // unparseable cap falls back to a full bar rather than NaN%.
            const capNum = Number(cap);
            const meterPct = Number.isFinite(capNum) && capNum > 0
              ? Math.min(100, Math.round((total / capNum) * 100))
              : 100;

            return (
              <Modal
                open={seatWallOpen}
                onClose={() => setSeatWallOpen(false)}
                title={t('seatwall_title', { cap })}
                footer={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ flex: '0 0 auto', display: 'flex', width: '14px', height: '14px' }}>
                      <Icon source={LockIcon} tone="subdued" />
                    </span>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t('seatwall_reassurance')}
                    </Text>
                  </div>
                }
                secondaryActions={[
                  {
                    content: t('seatwall_primary'),
                    disabled: seatWallBusy,
                    onAction: () => { setSeatWallOpen(false); navigate('/Pricing'); },
                  },
                  { content: t('seatwall_secondary'), disabled: seatWallBusy, onAction: () => setSeatWallOpen(false) },
                ]}
              >
                <Modal.Section>
                  <BlockStack gap="300">
                    {/* The limit, shown rather than described. Both numbers come
                        from getStaff(), so this is the real constraint - never a
                        manufactured one. */}
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        marginBottom: '6px',
                      }}>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {t('seatwall_seats_used')}
                        </Text>
                        <Text as="span" variant="bodySm" fontWeight="semibold">
                          {`${total} / ${cap}`}
                        </Text>
                      </div>
                      <div style={{
                        height: '6px',
                        borderRadius: '999px',
                        background: '#e3e3e3',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${meterPct}%`,
                          background: '#29845a',
                          borderRadius: '999px',
                        }} />
                      </div>
                    </div>

                    {/* One recommendation, priced, rather than a plan grid to
                        re-read. Figures come from the pricing catalogue. */}
                    <div
                      style={{
                        border: '1px solid #e3e3e3',
                        borderRadius: '12px',
                        background: '#ffffff',
                        boxShadow: '0 1px 2px rgba(26, 26, 26, 0.07), 0 2px 8px rgba(26, 26, 26, 0.05)',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ padding: '16px 18px 18px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                        }}>
                          <Text as="h3" variant="headingMd">
                            {t(plan.nameKey)}
                          </Text>
                          <Badge tone="success" icon={StarFilledIcon}>{t('seatwall_recommended')}</Badge>
                        </div>

                        {/* The price IS the decision being asked for. Give it the
                            weight, instead of burying it mid-sentence. */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: '6px',
                          marginTop: '10px',
                        }}>
                          <Text as="span" variant="heading2xl">
                            {formatUsd(plan.price)}
                          </Text>
                          <Text as="span" variant="bodyMd" tone="subdued">
                            {t('per_month')}
                          </Text>
                        </div>

                        {/* Capacity and per-seat price on one line. $24.99 reads
                            as expensive; $5.00 an employee reads against what
                            that employee costs an hour. */}
                        <div style={{ marginTop: '4px' }}>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t('seatwall_capacity', {
                              staff: plan.staff,
                              perSeat: formatUsd(plan.price / plan.staff),
                            })}
                          </Text>
                        </div>

                        <div style={{ height: '1px', background: '#e3e3e3', margin: '12px 0' }} />

                        <BlockStack gap="150">
                          {plan.highlightKeys.map((highlight) => (
                            <div key={highlight} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {/* .Polaris-Icon carries margin:auto, so a bare Icon
                                  as a flex child absorbs the whole row. Pin it
                                  inside the disc rather than sizing it directly. */}
                              <span style={{
                                flex: '0 0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: '#cdfee1',
                              }}>
                                <span style={{ display: 'flex', width: '13px', height: '13px' }}>
                                  <Icon source={CheckIcon} tone="success" />
                                </span>
                              </span>
                              <Text as="span" variant="bodySm">{t(highlight)}</Text>
                            </div>
                          ))}
                        </BlockStack>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginTop: '12px',
                          padding: '10px 12px',
                          borderRadius: '10px',
                          background: '#f0fdf6',
                          border: '1px solid #cdfee1',
                        }}>
                          <span style={{
                            flex: '0 0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '26px',
                            height: '26px',
                            borderRadius: '7px',
                            background: '#cdfee1',
                          }}>
                            <span style={{ display: 'flex', width: '16px', height: '16px' }}>
                              <Icon source={TeamIcon} tone="success" />
                            </span>
                          </span>
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            {t('seatwall_unlock_title')}
                          </Text>
                        </div>

                        {/* The action sits with the plan it buys. */}
                        <div style={{ marginTop: '14px' }}>
                          <Button
                            variant="primary"
                            fullWidth
                            size="large"
                            loading={seatWallBusy}
                            onClick={async () => {
                              setSeatWallBusy(true);
                              setSeatWallError('');
                              try {
                                await startSeatWallCheckout(plan);
                                setSeatWallOpen(false);
                              } catch (error) {
                                setSeatWallError(t('seatwall_failed'));
                              } finally {
                                setSeatWallBusy(false);
                              }
                            }}
                          >
                            {trialDays > 0
                              ? t('seatwall_start_trial', { days: trialDays })
                              : t('seatwall_subscribe')}
                          </Button>
                        </div>

                        {trialDays > 0 && (
                          <div style={{ marginTop: '8px', textAlign: 'center' }}>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {t('seatwall_trial_note', { days: trialDays })}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>

                    {seatWallError && <Banner tone="critical">{seatWallError}</Banner>}
                  </BlockStack>
                </Modal.Section>
              </Modal>
            );
          })()}
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default StaffManagement;
